import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { CodedInput } from "@/modules/add-new-data/components/coded-input";
import { ScrollableFormShell } from "@/modules/add-new-data/components/scrollable-form-shell";
import { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import type { RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import { cn } from "@/lib/utils";

type RbimHouseholdQuestionsStepProps = {
  errors: FieldErrors<AddNewDataFormValues>;
  family: AddNewDataFormValues["household"]["family"];
  register: UseFormRegister<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
  values: AddNewDataFormValues["householdQuestions"];
};

type FormFieldName = Parameters<UseFormRegister<AddNewDataFormValues>>[0];

const inputClass =
  "h-9 rounded-none border-slate-500 bg-white px-2 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

export function RbimHouseholdQuestionsStep({
  errors,
  family,
  register,
  setValue,
  values,
}: RbimHouseholdQuestionsStepProps) {
  const householdErrors = errors.householdQuestions;


  const autoNaOptions = {
    shouldDirty: true,
    shouldTouch: false,
    shouldValidate: true,
  } as const;
  /*
   * Q55 is answered only for Family A.
   *
   * Families B-J inherit the household toilet-facility information
   * already supplied for Family A, so the encoder must not answer
   * Q55 again.
   */
  const q46To57InheritedFromFamilyA =
    /^[B-J]$/i.test(String(family ?? "").trim());

  // Keep the existing Q55 behavior aligned with the broader
  // Q46-Q62 Family A inheritance rule.
  const q55InheritedFromFamilyA =
    q46To57InheritedFromFamilyA;

  const q55AutoNaRef = useRef(false);

  useEffect(() => {
    const currentToiletFacility =
      String(values.toiletFacility ?? "").trim();

    if (q55InheritedFromFamilyA) {
      q55AutoNaRef.current = true;

      if (currentToiletFacility !== "99 - N/A") {
        setValue(
          "householdQuestions.toiletFacility",
          "99 - N/A",
          autoNaOptions,
        );
      }

      return;
    }

    /*
     * When the encoder changes B-J back to Family A, clear only
     * the N/A value that this rule generated automatically.
     *
     * A manually selected 99 - N/A on an ordinary Family A record
     * is preserved because q55AutoNaRef will be false.
     */
    if (
      q55AutoNaRef.current &&
      currentToiletFacility === "99 - N/A"
    ) {
      q55AutoNaRef.current = false;

      setValue(
        "householdQuestions.toiletFacility",
        "",
        autoNaOptions,
      );

      return;
    }

    q55AutoNaRef.current = false;
  }, [
    q55InheritedFromFamilyA,
    setValue,
    values.toiletFacility,
  ]);

  const readCode = (value: unknown) => {
    const normalized = String(value ?? "").trim();
    const match = normalized.match(/^0*(\d+)/);
    return match?.[1] ?? normalized;
  };

  const clearAutoNa = (
    field:
      | "householdQuestions.femaleDeathAge"
      | "householdQuestions.femaleDeathCause"
      | "householdQuestions.childDeathAge"
      | "householdQuestions.childDeathSex"
      | "householdQuestions.childDeathCause",
    currentValue: string | undefined,
  ) => {
    const normalizedValue = String(currentValue ?? "").trim();
    const isAutoNa =
      normalizedValue === "99 - N/A" ||
      (field === "householdQuestions.childDeathSex" && normalizedValue === "99");

    if (isAutoNa) {
      setValue(field, "", autoNaOptions);
    }
  };

  const q58No =
    readCode(
      values.femaleDeathPast12Months,
    ) === "2";

  const q59No =
    readCode(
      values.childDeathPast12Months,
    ) === "2";
  const q58Registration = register(
    "householdQuestions.femaleDeathPast12Months",
    {
      onChange: (event) => {
        const code = readCode(event.target?.value);

        if (code === "2") {
          setValue(
            "householdQuestions.femaleDeathAge",
            "99 - N/A",
            autoNaOptions,
          );
          setValue(
            "householdQuestions.femaleDeathCause",
            "99 - N/A",
            autoNaOptions,
          );
          return;
        }

        if (code === "1") {
          clearAutoNa(
            "householdQuestions.femaleDeathAge",
            values.femaleDeathAge,
          );
          clearAutoNa(
            "householdQuestions.femaleDeathCause",
            values.femaleDeathCause,
          );
        }
      },
    },
  );

  const q59Registration = register(
    "householdQuestions.childDeathPast12Months",
    {
      onChange: (event) => {
        const code = readCode(event.target?.value);

        if (code === "2") {
          setValue(
            "householdQuestions.childDeathAge",
            "99 - N/A",
            autoNaOptions,
          );
          setValue(
            "householdQuestions.childDeathSex",
            "99 - N/A",
            autoNaOptions,
          );
          setValue(
            "householdQuestions.childDeathCause",
            "99 - N/A",
            autoNaOptions,
          );
          return;
        }

        if (code === "1") {
          clearAutoNa(
            "householdQuestions.childDeathAge",
            values.childDeathAge,
          );
          clearAutoNa(
            "householdQuestions.childDeathSex",
            values.childDeathSex,
          );
          clearAutoNa(
            "householdQuestions.childDeathCause",
            values.childDeathCause,
          );
        }
      },
    },
  );
  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-950">Pages 10 - 11: Questions for the Household</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {q46To57InheritedFromFamilyA
            ? "Q46 to Q57 were already provided by Family A. Q58 to Q62 remain available for household-level encoding."
            : "Encode household-level RBIM questions Q46 to Q62 from the final questionnaire page."}
        </p>
      </div>

      <ScrollableFormShell
        contentClassName="bg-white p-3 sm:p-4"
        minWidthClassName="min-w-0"
        scrollerClassName="overflow-x-hidden"
      >
        <div className="border border-slate-700">
          <SectionTitle>H. Questions for the Household</SectionTitle>
          <div className="grid min-w-0 grid-cols-1 lg:grid-cols-2">
            <fieldset
              className={cn(
                "contents",
                q46To57InheritedFromFamilyA && "opacity-60",
              )}
              disabled={q46To57InheritedFromFamilyA}
            >
            <QuestionCodeInput
              code="Q46"
              label="Housing unit tenure"
              question="What is the tenure status of this housing unit?"
              name="householdQuestions.housingTenure"
              questionKey="q46_housing_unit_tenure"
              register={register}
              value={values.housingTenure}
              error={householdErrors?.housingTenure?.message}
            />
            <QuestionCodeInput
              code="Q47"
              label="Lot tenure"
              question="What is the tenure status of the lot?"
              name="householdQuestions.lotTenure"
              questionKey="q47_lot_tenure"
              register={register}
              value={values.lotTenure}
              error={householdErrors?.lotTenure?.message}
            />
            <QuestionCodeInput
              code="Q48"
              label="Hazard-prone area"
              question="Is the household located in a hazard-prone area?"
              name="householdQuestions.hazardProneArea"
              questionKey="q48_hazard_prone_area"
              register={register}
              value={values.hazardProneArea}
              error={householdErrors?.hazardProneArea?.message}
            />
            <QuestionCodeInput
              code="Q49"
              label="Disaster experienced in past 5 years"
              question="What disaster has the household experienced in the past 5 years?"
              name="householdQuestions.disasterExperiencedPastFiveYears"
              questionKey="q49_disaster_experienced_past_5_years"
              register={register}
              value={values.disasterExperiencedPastFiveYears}
              error={householdErrors?.disasterExperiencedPastFiveYears?.message}
            />
            <QuestionCodeInput
              code="Q50"
              label="Knows nearest evacuation center"
              question="Does the household know the nearest evacuation center?"
              name="householdQuestions.knowsNearestEvacuationCenter"
              questionKey="q50_knows_nearest_evacuation_center"
              register={register}
              value={values.knowsNearestEvacuationCenter}
              error={householdErrors?.knowsNearestEvacuationCenter?.message}
            />
            <QuestionCodeInput
              code="Q51"
              label="Fuel for lighting"
              question="What type of fuel does this household use for lighting?"
              name="householdQuestions.lightingFuel"
              questionKey="q51_fuel_lighting"
              register={register}
              value={values.lightingFuel}
              error={householdErrors?.lightingFuel?.message}
            />
            <QuestionCodeInput
              code="Q52"
              label="Fuel for cooking"
              question="What kind of fuel does this household use most of the time for cooking?"
              name="householdQuestions.cookingFuel"
              questionKey="q52_fuel_cooking"
              register={register}
              value={values.cookingFuel}
              error={householdErrors?.cookingFuel?.message}
            />
            <QuestionCodeInput
              code="Q53"
              label="Main source of drinking water"
              question="What is the household's main source of drinking water?"
              name="householdQuestions.drinkingWaterSource"
              questionKey="q53_drinking_water_source"
              register={register}
              value={values.drinkingWaterSource}
              error={householdErrors?.drinkingWaterSource?.message}
            />
            <div className="grid min-w-0 grid-cols-1 border-b border-slate-700 xl:grid-cols-2">
              <QuestionCodeInput
                code="Q54a"
                label="Garbage disposal"
                question="How does the household usually dispose of kitchen garbage?"
                name="householdQuestions.garbageDisposal"
                questionKey="q54a_kitchen_garbage_disposal"
                register={register}
                value={values.garbageDisposal}
                error={householdErrors?.garbageDisposal?.message}
                className="border-b-0"
              />
              <QuestionCodeInput
                code="Q54b"
                label="Garbage segregation"
                question="Do you segregate your garbage?"
                name="householdQuestions.segregatesGarbage"
                questionKey="q54b_garbage_segregation"
                register={register}
                value={values.segregatesGarbage}
                error={householdErrors?.segregatesGarbage?.message}
                className="border-b-0"
              />
            </div>
            <fieldset
              className="contents"
              disabled={q55InheritedFromFamilyA}
            >
              <QuestionCodeInput
                code="Q55"
                label="Toilet facility"
                question={
                  q55InheritedFromFamilyA
                    ? "Already provided by Family A. No answer required."
                    : "What type of toilet facility does this household use?"
                }
                name="householdQuestions.toiletFacility"
                questionKey="q55_toilet_facility_final"
                register={register}
                value={
                  q55InheritedFromFamilyA
                    ? "99 - N/A"
                    : values.toiletFacility
                }
                error={
                  q55InheritedFromFamilyA
                    ? undefined
                    : householdErrors?.toiletFacility?.message
                }
                className={
                  q55InheritedFromFamilyA
                    ? "opacity-60"
                    : undefined
                }
              />
            </fieldset>
            <QuestionCodeInput
              code="Q56"
              label="Type of building/house"
              question="Do not ask. Observation only."
              name="householdQuestions.buildingType"
              questionKey="q56_building_type"
              register={register}
              value={values.buildingType}
              error={householdErrors?.buildingType?.message}
            />
            <QuestionCodeInput
              code="Q57"
              label="Outer wall construction materials"
              question="Do not ask. Observation only."
              name="householdQuestions.wallMaterial"
              questionKey="q57_wall_material"
              register={register}
              value={values.wallMaterial}
              error={householdErrors?.wallMaterial?.message}
            />
            </fieldset>

            <QuestionBlock
              code="Q58"
              label="Female member aged 10-54 died in past 12 months"
              question="Was there a female member aged 10 to 54 who died in the past 12 months?"
              error={householdErrors?.femaleDeathPast12Months?.message}
            >
              <div className="grid min-w-0 gap-2 sm:grid-cols-[110px_80px_minmax(0,1fr)] min-[1600px]:grid-cols-[120px_90px_minmax(0,1fr)]">
                <CodedInput
                  className={inputClass}
                  inputMode="numeric"
                  placeholder="1"
                  questionKey="q58_female_death_past_12_months"
                  registration={q58Registration}
                  value={values.femaleDeathPast12Months}
                />
                <fieldset
                  className="contents"
                  disabled={q58No}
                >
                  <AgeInput
                    error={
                      q58No
                        ? undefined
                        : householdErrors?.femaleDeathAge?.message
                    }
                    registration={register(
                      "householdQuestions.femaleDeathAge",
                    )}
                  />
                  <Input
                    autoUppercase
                    className={inputClass}
                    placeholder="Cause of death"
                    {...register(
                      "householdQuestions.femaleDeathCause",
                    )}
                  />
                </fieldset>
              </div>
            </QuestionBlock>
            <QuestionBlock
              code="Q59"
              label="Child below 5 death in past 12 months"
              question="Do you have a child below 5 years old who died in the past 12 months?"
              error={householdErrors?.childDeathPast12Months?.message ?? householdErrors?.childDeathSex?.message}
            >
              <div className="grid min-w-0 gap-2 sm:grid-cols-[100px_70px_80px_minmax(0,1fr)] min-[1600px]:grid-cols-[120px_80px_90px_minmax(0,1fr)]">
                <CodedInput
                  className={inputClass}
                  inputMode="numeric"
                  placeholder="1"
                  questionKey="q59_child_death_past_12_months"
                  registration={q59Registration}
                  value={values.childDeathPast12Months}
                />
                <fieldset
                  className="contents"
                  disabled={q59No}
                >
                  <AgeInput
                    error={
                      q59No
                        ? undefined
                        : householdErrors?.childDeathAge?.message
                    }
                    registration={register(
                      "householdQuestions.childDeathAge",
                    )}
                  />
                  <CodedInput
                    className={inputClass}
                    inputMode="numeric"
                    placeholder="1"
                    questionKey="q59_child_death_sex"
                    registration={register(
                      "householdQuestions.childDeathSex",
                    )}
                    value={values.childDeathSex}
                  />
                  <Input
                    autoUppercase
                    className={inputClass}
                    placeholder="Cause of death"
                    {...register(
                      "householdQuestions.childDeathCause",
                    )}
                  />
                </fieldset>
              </div>
            </QuestionBlock>
            <QuestionBlock
              code="Q60"
              label="Common diseases that cause death"
              question="What do you think are the common diseases that cause death in this barangay?"
            >
              <StackedInputs
                names={[
                  "householdQuestions.commonDiseaseCause1",
                  "householdQuestions.commonDiseaseCause2",
                  "householdQuestions.commonDiseaseCause3",
                ]}
                register={register}
              />
            </QuestionBlock>
            <QuestionBlock
              code="Q61"
              label="Primary needs"
              question="What do you think are the primary needs of this barangay?"
            >
              <StackedInputs
                names={[
                  "householdQuestions.primaryNeed1",
                  "householdQuestions.primaryNeed2",
                  "householdQuestions.primaryNeed3",
                ]}
                register={register}
              />
            </QuestionBlock>
            <QuestionBlock
              code="Q62"
              label="Expected residence five years from now"
              question="Where does your household intend to stay five years from now?"
              className="lg:col-span-2"
            >
              <div className="grid min-w-0 gap-2 sm:grid-cols-3">
                <Input autoUppercase className={inputClass} placeholder="Barangay" {...register("householdQuestions.futureBarangay")} />
                <Input
                  autoUppercase
                  className={inputClass}
                  placeholder="City/Municipality"
                  {...register("householdQuestions.futureMunicipality")}
                />
                <Input autoUppercase className={inputClass} placeholder="Province" {...register("householdQuestions.futureProvince")} />
              </div>
            </QuestionBlock>
          </div>
        </div>
      </ScrollableFormShell>
    </div>
  );
}

function AgeInput({
  error,
  registration,
}: {
  error?: string;
  registration: ReturnType<UseFormRegister<AddNewDataFormValues>>;
}) {
  return (
    <div className="min-w-0">
      <Input
        aria-invalid={Boolean(error)}
        className={cn(inputClass, error && "border-red-600")}
        inputMode="numeric"
        placeholder="Age"
        {...registration}
      />
      {error ? <p className="mt-1 text-[11px] font-semibold leading-tight text-red-700">{String(error)}</p> : null}
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="border-b border-slate-700 bg-[#eeeeee] px-3 py-2 text-center text-sm font-extrabold uppercase tracking-normal text-slate-950">
      {children}
    </div>
  );
}

function QuestionCodeInput({
  code,
  label,
  question,
  name,
  questionKey,
  register,
  value,
  error,
  className,
}: {
  code: string;
  label: string;
  question: string;
  name: FormFieldName;
  questionKey: RbimCodeQuestionKey;
  register: UseFormRegister<AddNewDataFormValues>;
  value?: string;
  error?: string;
  className?: string;
}) {
  return (
    <QuestionBlock code={code} label={label} question={question} error={error} className={className}>
      <CodedInput
        className={inputClass}
        placeholder="Code"
        questionKey={questionKey}
        registration={register(name)}
        value={value}
      />
    </QuestionBlock>
  );
}

function QuestionBlock({
  code,
  label,
  question,
  children,
  error,
  className,
}: {
  code: string;
  label: string;
  question: string;
  children: ReactNode;
  error?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "min-h-[142px] min-w-0 border-b border-r border-slate-700 p-3",
        error && "bg-red-50 ring-1 ring-inset ring-red-500",
        className,
      )}
    >
      <div className="mb-3 grid grid-cols-[60px_minmax(0,1fr)] gap-3 min-[1600px]:grid-cols-[70px_minmax(0,1fr)]">
        <div className="text-sm font-extrabold text-slate-950">{code}</div>
        <div>
          <p className="text-sm font-bold uppercase leading-tight text-slate-950">{label}</p>
          <p className="mt-1 text-[11px] leading-snug text-slate-700">{question}</p>
        </div>
      </div>
      {children}
      {error ? <p className="mt-2 text-xs font-semibold text-red-700">{String(error)}</p> : null}
    </div>
  );
}

function StackedInputs({
  names,
  register,
}: {
  names: [FormFieldName, FormFieldName, FormFieldName];
  register: UseFormRegister<AddNewDataFormValues>;
}) {
  return (
    <div className="space-y-2">
      {names.map((name, index) => (
        <Input key={name} autoUppercase className={inputClass} placeholder={`${index + 1}.`} {...register(name)} />
      ))}
    </div>
  );
}
